import type { GeoPoint } from './geo';

export interface CurrentWeather {
  temperature: number;
  apparent: number;
  humidity: number;
  precipitation: number;
  weatherCode: number;
  cloudCover: number;
  pressure: number;
  windSpeed: number;
  windDirection: number;
  isDay: boolean;
  time: string;
  timezone: string;
}

/** Fetch current weather from Open-Meteo (free, worldwide, no API key). */
export async function fetchWeather(loc: GeoPoint, signal?: AbortSignal): Promise<CurrentWeather> {
  const params = new URLSearchParams({
    latitude: loc.lat.toFixed(4),
    longitude: loc.lon.toFixed(4),
    current:
      'temperature_2m,relative_humidity_2m,apparent_temperature,is_day,precipitation,weather_code,cloud_cover,surface_pressure,wind_speed_10m,wind_direction_10m',
    timezone: 'auto',
  });
  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`, {
    signal: signal ?? AbortSignal.timeout(12000),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as {
    current: Record<string, number | string>;
    timezone?: string;
  };
  const c = data.current;
  return {
    temperature: Number(c.temperature_2m),
    apparent: Number(c.apparent_temperature),
    humidity: Number(c.relative_humidity_2m),
    precipitation: Number(c.precipitation),
    weatherCode: Number(c.weather_code),
    cloudCover: Number(c.cloud_cover),
    pressure: Number(c.surface_pressure),
    windSpeed: Number(c.wind_speed_10m),
    windDirection: Number(c.wind_direction_10m),
    isDay: Number(c.is_day) === 1,
    time: String(c.time),
    timezone: data.timezone ?? 'UTC',
  };
}

export type WeatherIconKey =
  | 'sun'
  | 'moon'
  | 'cloud-sun'
  | 'cloud-moon'
  | 'cloud'
  | 'fog'
  | 'drizzle'
  | 'rain'
  | 'snow'
  | 'storm'
  | 'thermometer';

const WMO_CODES: Record<number, { label: string; icon: WeatherIconKey }> = {
  0: { label: 'Clear sky', icon: 'sun' },
  1: { label: 'Mainly clear', icon: 'cloud-sun' },
  2: { label: 'Partly cloudy', icon: 'cloud-sun' },
  3: { label: 'Overcast', icon: 'cloud' },
  45: { label: 'Fog', icon: 'fog' },
  48: { label: 'Depositing rime fog', icon: 'fog' },
  51: { label: 'Light drizzle', icon: 'drizzle' },
  53: { label: 'Drizzle', icon: 'drizzle' },
  55: { label: 'Dense drizzle', icon: 'rain' },
  56: { label: 'Freezing drizzle', icon: 'rain' },
  57: { label: 'Dense freezing drizzle', icon: 'rain' },
  61: { label: 'Light rain', icon: 'drizzle' },
  63: { label: 'Rain', icon: 'rain' },
  65: { label: 'Heavy rain', icon: 'rain' },
  66: { label: 'Freezing rain', icon: 'rain' },
  67: { label: 'Heavy freezing rain', icon: 'rain' },
  71: { label: 'Light snow', icon: 'snow' },
  73: { label: 'Snow', icon: 'snow' },
  75: { label: 'Heavy snow', icon: 'snow' },
  77: { label: 'Snow grains', icon: 'snow' },
  80: { label: 'Light rain showers', icon: 'drizzle' },
  81: { label: 'Rain showers', icon: 'rain' },
  82: { label: 'Violent rain showers', icon: 'storm' },
  85: { label: 'Snow showers', icon: 'snow' },
  86: { label: 'Heavy snow showers', icon: 'snow' },
  95: { label: 'Thunderstorm', icon: 'storm' },
  96: { label: 'Thunderstorm with hail', icon: 'storm' },
  99: { label: 'Thunderstorm with heavy hail', icon: 'storm' },
};

const NIGHT_ICONS: Partial<Record<WeatherIconKey, WeatherIconKey>> = {
  sun: 'moon',
  'cloud-sun': 'cloud-moon',
};

export function weatherInfo(code: number, isDay: boolean): { label: string; icon: WeatherIconKey } {
  const base = WMO_CODES[code] ?? { label: `Weather code ${code}`, icon: 'thermometer' };
  if (!isDay) {
    return { label: base.label, icon: NIGHT_ICONS[base.icon] ?? base.icon };
  }
  return base;
}
